import { MoreHorizontal, Eye, SquarePen, Copy, Globe, Lock, Trash } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { OutlineTemplate } from '@/lib/api'

interface TemplateListRowProps {
  template: OutlineTemplate
  currentUserId: string
  isAdmin: boolean
  onPreview: (template: OutlineTemplate) => void
  onEdit: (template: OutlineTemplate) => void
  onDuplicate: (template: OutlineTemplate) => void
  onToggleVisibility: (template: OutlineTemplate) => void
  onDelete: (template: OutlineTemplate) => void
}

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  return date.toLocaleDateString()
}

export function TemplateListRow({
  template,
  currentUserId,
  isAdmin,
  onPreview,
  onEdit,
  onDuplicate,
  onToggleVisibility,
  onDelete,
}: TemplateListRowProps) {
  const isCreator = template.createdBy === currentUserId

  const canEdit = isCreator
  const canDelete = isCreator || (isAdmin && template.visibility === 'shared')
  const canToggleVisibility = isCreator

  return (
    <tr
      className="border-b last:border-b-0 hover:bg-muted/50 cursor-pointer group transition-colors"
      onClick={() => onPreview(template)}
    >
      {/* Name + description */}
      <td className="px-4 py-3">
        <p className="text-sm font-medium truncate">{template.name}</p>
        {template.description && (
          <p className="text-meta truncate mt-0.5">{template.description}</p>
        )}
      </td>

      {/* Section count */}
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {template.sections.length}
      </td>

      {/* Visibility */}
      <td className="px-4 py-3">
        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
          {template.visibility === 'shared' ? 'Shared' : 'Private'}
        </Badge>
      </td>

      {/* Creator */}
      <td className="px-4 py-3 text-sm text-muted-foreground truncate">
        {isCreator ? 'You' : template.creatorName}
      </td>

      {/* Updated */}
      <td className="px-4 py-3 text-sm text-muted-foreground text-right">
        {formatRelativeDate(template.updatedAt)}
      </td>

      {/* Actions */}
      <td className="px-4 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => onPreview(template)}>
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </DropdownMenuItem>
            {canEdit && (
              <DropdownMenuItem onClick={() => onEdit(template)}>
                <SquarePen className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onDuplicate(template)}>
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            {canToggleVisibility && (
              <DropdownMenuItem onClick={() => onToggleVisibility(template)}>
                {template.visibility === 'private' ? (
                  <>
                    <Globe className="h-4 w-4 mr-2" />
                    Share with organisation
                  </>
                ) : (
                  <>
                    <Lock className="h-4 w-4 mr-2" />
                    Set to private
                  </>
                )}
              </DropdownMenuItem>
            )}
            {canDelete && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => onDelete(template)}
                >
                  <Trash className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  )
}
